import { useState } from 'react';
import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Avatar,
  Tooltip,
  Chip,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InsightsIcon from '@mui/icons-material/Insights';
import NatureIcon from '@mui/icons-material/Nature';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import SpeedIcon from '@mui/icons-material/Speed';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import primaryLogo from '../images/BrightFuturePower.png';

// Navigation items with icons
const navigationItems = [
  { name: 'Overview', path: '/overview', icon: <DashboardIcon fontSize="small" />, color: '#10B981' },
  { name: 'Insights', path: '/insights', icon: <InsightsIcon fontSize="small" />, color: '#F59E0B' },
  { name: 'Seasonal Patterns', path: '/seasonal-insights', icon: <WbSunnyIcon fontSize="small" />, color: '#F97316' },
  { name: 'Environmental Impact', path: '/carbon-impact', icon: <NatureIcon fontSize="small" />, color: '#4ADE80' },
  { name: 'Achievements', path: '/achievements', icon: <EmojiEventsIcon fontSize="small" />, color: '#3B82F6' },
  { name: 'Efficiency Metrics (Beta)', path: '/efficiency-metrics', icon: <SpeedIcon fontSize="small" />, color: '#8B5CF6' },
  { name: 'AI Query Builder', path: '/query-builder', icon: <SmartToyIcon fontSize="small" />, color: '#10B981' },
];

interface HeaderProps {
  businessId: string | null;
  onLogout: () => void;
  timeFrame?: number;
  onTimeFrameChange?: (value: number) => void;
}

const Header = ({ businessId, onLogout }: HeaderProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  
  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // User menu state
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchorEl);
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };
  
  const handleLogout = () => {
    handleUserMenuClose();
    onLogout();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  // Mobile drawer content
  const drawerContent = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pb: 2 }}>
        <Box
          component="img"
          src={primaryLogo}
          alt="Bright Future Power Logo"
          sx={{ height: 36, width: 'auto' }}
        />
        <IconButton onClick={handleDrawerToggle} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider />
      
      <List sx={{ pt: 2 }}>
        {navigationItems.map((item) => {
          const isActive = location.pathname === item.path;
          
          return (
            <ListItem key={item.name} disablePadding>
              <ListItemButton 
                onClick={() => handleNavigate(item.path)}
                sx={{
                  py: 1.5,
                  borderLeft: isActive ? `4px solid ${item.color}` : '4px solid transparent',
                  bgcolor: isActive ? alpha(item.color, 0.08) : 'transparent',
                  '&:hover': {
                    bgcolor: alpha(item.color, 0.05),
                  }
                }}
              >
                <ListItemIcon sx={{ color: item.color, minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.name} 
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'text.primary' : 'text.secondary',
                  }}
                />
                {isActive && <ChevronRightIcon sx={{ color: item.color }} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{
        backgroundColor: 'primary.main',
        borderBottom: '1px solid',
        borderColor: alpha('#FFFFFF', 0.1),
        boxShadow: `0 1px 10px ${alpha('#000000', 0.1)}`,
      }}
    >
      <Toolbar disableGutters sx={{ py: 1, justifyContent: 'space-between', px: { xs: 1, md: 2 } }}>
        {/* Logo - Left */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': {
              textDecoration: 'none',
            },
          }}
          component={RouterLink}
          to={businessId ? '/overview' : '/'}
          className="no-underline-link"
        >
          <Box
            component="img"
            src={primaryLogo}
            alt="Bright Future Power Logo"
            sx={{
              height: 40,
              width: 'auto',
            }}
          />
        </Box>

        {/* Desktop navigation */}
        {!isMobile && businessId && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            gap: { sm: 0.25, md: 0.5 },
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto',
            flexWrap: 'nowrap'
          }}>
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path;
              
              // Create shorter names for medium screens
              const getResponsiveLabel = () => {
                if (item.name === 'Seasonal Patterns') return (
                  <React.Fragment>
                    <Box sx={{ display: { sm: 'none', md: 'block' } }}>Seasonal Patterns</Box>
                    <Box sx={{ display: { sm: 'block', md: 'none' } }}>Seasonal</Box>
                  </React.Fragment>
                );
                if (item.name === 'Environmental Impact') return (
                  <React.Fragment>
                    <Box sx={{ display: { sm: 'none', md: 'block' } }}>Environmental Impact</Box>
                    <Box sx={{ display: { sm: 'block', md: 'none' } }}>Environment</Box>
                  </React.Fragment>
                );
                if (item.name === 'Efficiency Metrics (Beta)') return (
                  <React.Fragment>
                    <Box sx={{ display: { sm: 'none', md: 'block' } }}>Efficiency Metrics (Beta)</Box>
                    <Box sx={{ display: { sm: 'block', md: 'none' } }}>Efficiency</Box>
                  </React.Fragment>
                );
                return item.name;
              };
              
              return (
                <Button
                  key={item.name}
                  component={RouterLink}
                  to={item.path}
                  startIcon={
                    <Box sx={{ 
                      color: item.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {item.icon}
                    </Box>
                  }
                  sx={{
                    color: isActive ? '#FFFFFF' : alpha('#FFFFFF', 0.75),
                    px: { sm: 0.75, md: 1.5 },
                    py: 1,
                    fontSize: { sm: '0.75rem', md: '0.875rem' },
                    borderRadius: 2,
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    fontWeight: isActive ? 600 : 500,
                    backgroundColor: isActive ? alpha('#FFFFFF', 0.1) : 'transparent',
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: alpha('#FFFFFF', 0.15),
                      color: '#FFFFFF',
                      transform: 'translateY(-2px)',
                    },
                    ...(isActive && {
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 6,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '30%',
                        height: '3px',
                        borderRadius: '3px',
                        backgroundColor: item.color,
                      }
                    })
                  }}
                >
                  {getResponsiveLabel()}
                </Button>
              );
            })}
          </Box>
        )}

        {/* Right side elements */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Mobile menu button */}
          {isMobile && businessId && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* User profile */}
          {businessId && (
            <Box>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleUserMenu}
                  size="small"
                  sx={{ ml: 1 }}
                  aria-controls={userMenuOpen ? 'user-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={userMenuOpen ? 'true' : undefined}
                >
                  <Avatar 
                    alt="User Profile"
                    sx={{ 
                      bgcolor: 'secondary.main',
                      width: 36,
                      height: 36,
                      boxShadow: '0 0 0 2px #FFFFFF'
                    }}
                  >
                    <AccountCircleIcon />
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                id="user-menu"
                anchorEl={userMenuAnchorEl}
                open={userMenuOpen}
                onClose={handleUserMenuClose}
                onClick={handleUserMenuClose}
                PaperProps={{
                  elevation: 2,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                    mt: 1.5,
                    borderRadius: 2,
                    minWidth: 180,
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Green Earth Cafe</Typography>
                  <Typography variant="body2" color="text.secondary">BIZ001</Typography>
                </Box>
                
                <Divider />
                
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Toolbar>
      
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
};

export default Header; 
